import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { LinkButton } from '../ui/Button';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL, SERVICE_AREA, company } from '../../data/site';
import { telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const tagline = ['Multiservice', 'Montage', 'Vedligehold'];
export function Hero() {
  const {
    settings
  } = useAdminData();
  return <section id="top" className="scroll-mt-24" data-id="element-765" data-mp="qukk7ne1ajdl">
      <div className="bg-white" data-id="element-766" data-mp="qt9f9k8ur5kw">
        <div className="mx-auto grid max-w-content items-center gap-10 px-5 py-14 md:py-20 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:px-8" data-id="element-767" data-mp="vlu9unzl1mph">
          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.45,
          ease
        }} className="flex justify-center lg:justify-start" data-id="element-768" data-mp="10kxa7v4mb4r4">
            <h1 data-id="element-769" data-mp="9nj9k9atqab5">
              <img src={company.logo} alt={`${settings.companyName} — multiservice, håndværk og vedligehold`} className="h-44 w-auto max-w-full sm:h-52 md:h-64 lg:h-72" data-id="element-770" data-mp="9o2qpnsnguo4" />
            </h1>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45,
          ease,
          delay: 0.08
        }} data-id="element-771" data-mp="3rtfhijwmorof">
            <p className="flex items-center gap-2 text-[15px] font-medium text-accent-deep" data-id="element-772" data-mp="sg50cf6vjaov">
              <MapPinIcon className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-773" data-mp="nhah5c4zvk48" />
              Multiservice i {SERVICE_AREA}
            </p>

            <h2 className="mt-3 max-w-xl font-display text-[clamp(1.75rem,4.5vw,2.75rem)] font-bold leading-[1.1] tracking-tight text-ink" data-id="element-774" data-mp="eluj0ix7iisu">
              Har du en praktisk opgave, du gerne vil have løst?
            </h2>

            <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft md:text-xl" data-id="element-775" data-mp="16cik5uyvro6i">
              Jeg hjælper dig med multiservice, montage, vedligeholdelse og mindre praktiske opgaver
              i {SERVICE_AREA}. Fortæl mig om din opgave — så ser vi på den sammen.
            </p>

            <ul className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[15px] text-ink-muted" data-id="element-776" data-mp="2qvkwysuex1vu">
              {tagline.map((item, index) => <li key={item} className="flex items-center gap-4" data-id="element-777" data-mp="2hvpee2yj24ls" data-mpi={index}>
                  {index > 0 && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" data-id="element-778" data-mp="2mv21motb9dow" />}
                  {item}
                </li>)}
            </ul>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-779" data-mp="2cw32pxyckwf3">
              <LinkButton href="#kontakt" size="lg" data-id="element-780" data-mp="2cyfj46ogiyq1">
                Få et tilbud
              </LinkButton>
              <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" data-id="element-781" data-mp="642bh0j3ivv5">
                Kontakt mig
              </LinkButton>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Kontaktbånd: fire lige tunge punkter, ens opsætning og ingen ombrydning. */}
      <div className="bg-ink" data-id="element-782" data-mp="1qluqlabot3ed">
        <div className="mx-auto max-w-content px-5 py-6 lg:px-8" data-id="element-783" data-mp="25dbyjquhema3">
          <dl className="grid grid-cols-2 gap-x-8 gap-y-6 md:flex md:items-center md:justify-between md:gap-10" data-id="element-784" data-mp="25ewqd8pevr49">
            {settings.phone && <div className="flex items-start gap-3" data-id="element-785" data-mp="2ecqkqe3m8aos">
                <PhoneIcon className="mt-[3px] h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} aria-hidden="true" data-id="element-786" data-mp="2efcwkpxjhcct" />
                <div className="min-w-0" data-id="element-787" data-mp="2o9lk0z7pqf44">
                  <dt className="text-xs uppercase tracking-wider text-white/45" data-id="element-788" data-mp="2jc3c9c5fm5xp">Telefon</dt>
                  <dd className="mt-0.5" data-id="element-789" data-mp="tx55lb4rjuo6">
                    <a href={telHref(settings.phone)} className="whitespace-nowrap text-[17px] font-medium text-white transition-colors duration-200 ease-smooth hover:text-accent" data-id="element-790" data-mp="ty6sccv565lq">
                      {settings.phone}
                    </a>
                  </dd>
                </div>
              </div>}

            <div className="flex items-start gap-3 max-[400px]:col-span-2" data-id="element-791" data-mp="10f8c5tbf3xl0">
              <MailIcon className="mt-[3px] h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} aria-hidden="true" data-id="element-792" data-mp="15djivv6f1rz0" />
              <div className="min-w-0" data-id="element-793" data-mp="cocewgo70a6d">
                <dt className="text-xs uppercase tracking-wider text-white/45" data-id="element-794" data-mp="cpdxr7bga0yn">E-mail</dt>
                <dd className="mt-0.5" data-id="element-795" data-mp="hn5ucen1ib96">
                  <a href={`mailto:${CONTACT_EMAIL}`} className="whitespace-nowrap text-[15px] font-medium text-white transition-colors duration-200 ease-smooth hover:text-accent md:text-[17px]" data-id="element-796" data-mp="hlcjen5e94vd">
                    {CONTACT_EMAIL}
                  </a>
                </dd>
              </div>
            </div>

            <div className="flex items-start gap-3" data-id="element-797" data-mp="276teymaasmce">
              <MapPinIcon className="mt-[3px] h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} aria-hidden="true" data-id="element-798" data-mp="236j7f56uxnvf" />
              <div className="min-w-0" data-id="element-799" data-mp="1y9j24whu0p5d">
                <dt className="text-xs uppercase tracking-wider text-white/45" data-id="element-800" data-mp="1t8v4hrz3la26">Område</dt>
                <dd className="mt-0.5 text-[17px] font-medium text-white" data-id="element-801" data-mp="1o90dzpj8imhn">{SERVICE_AREA}</dd>
              </div>
            </div>

            <div className="flex items-start gap-3" data-id="element-802" data-mp="1jhqmski1fb3n">
              <ClockIcon className="mt-[3px] h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} aria-hidden="true" data-id="element-803" data-mp="1fgydxsd2v87w" />
              <div className="min-w-0" data-id="element-804" data-mp="1afr5rs70w70p">
                <dt className="text-xs uppercase tracking-wider text-white/45" data-id="element-805" data-mp="15jafarccpgkg">Svar</dt>
                <dd className="mt-0.5 whitespace-nowrap text-[17px] font-medium text-white" data-id="element-806" data-mp="10jxx4jwfcxo7">
                  Normalt samme dag
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </div>
    </section>;
}